import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import PropTypes from 'prop-types';
import { FormattedMessage, intlShape } from 'react-intl';
import { fareShape, historyShape, productsShape, legShape } from 'constants/propTypes';
import moment from 'moment';

import { ConfirmButton } from 'components/ui/Button';
import Modal from 'components/ui/Modal';
import { UPDATE_EXTRAS, REBOOK, ADD_ADDITIONAL_DETAILS, OVERRIDE_PRICE } from 'store/mutations';
import AgentPortalError from 'errors/AgentPortalError';
import getReplaceItems from 'utils/getReplacedItems';
import useSummatedFaresOverrideMutation from 'components/mutations/useSummatedFaresOverrideMutation';
import { MOBILE_TERMINAL_ID_STORAGE_KEY } from 'constants/user';

import { isTryingToDowngrade } from '../../Exchange.utils';
import { buildESRequest, buildFeeRequest } from '../ExchangeConfirmSelection/ExchangeConfirmSelection.utils';

import { StyledActionButton } from './ExchangeConfirmSelectionWithWarning.styles';
import { getUpdateDetailsForOnHoldBooking } from './ExchangeConfirmSelectionWithWarning.utils';
import { OUTBOUND, INBOUND } from 'constants/booking';

const ExchangeConfirmSelectionWithWarning = ({
  onOpen,
  onClose,
  isOpen,
  history,
  data,
  disabled,
  intl: { formatMessage },
  overrideReason,
  isFlowForward,
  isStationAgent,
  stationName,
  isExchangingOFETrain,
  outboundOriginalClassOfService = '',
  inboundOriginalClassOfService = '',
  isLoyaltyBooking = false,
  hasSpecialLoyaltyTier = false,
  isMultiJourney = false,
  isSummatedFaresSelectionEnabled = false,
  isExchangingMultipleLegsOfAOutboundSummated = false,
  isExchangingSinglegOfAOutboundSummated = false,
  isExchangingMultipleLegsOfAInboundSummated = false,
  isExchangingSingleLegOfAInboundSummated = false,
  isExchangingOutboundDirectFare = false,
  isExchangingInboundDirectFare = false,
  isExchangingOutboundThroughFare = false,
  isExchangingInboundThroughFare = false,
  outboundOriginalClassesOfAllServices = [],
  inboundOriginalClassesOfAllServices = [],
  isProvisional = false,
  outboundLegsBeingExchanged = [],
  inboundLegsBeingExchanged = []
}) => {
  const [error, setError] = useState(null);
  const membershipNumber = new URLSearchParams(history?.location?.search).get('membershipNumber');

  const esRequest = buildESRequest({
    data,
    isExchangingOFETrain,
    isFlowForward,
    overrideReason,
    isLoyaltyBooking
  });

  const feeRequest = buildFeeRequest({
    data,
    isExchangingOFETrain,
    isStationAgent,
    overrideReason,
    isFlowForward,
    hasSpecialLoyaltyTier
  });

  const shouldShowDowngradeMessage = !!(
    isTryingToDowngrade(outboundOriginalClassesOfAllServices, data?.outboundSelectedFare) ||
    isTryingToDowngrade(inboundOriginalClassesOfAllServices, data?.inboundSelectedFare)
  );

  const hasFeeRequestSegments = feeRequest?.segments?.length !== 0;

  const redirectOnCompleted = () =>
    history.push(`/manage/bookings/${data.reference}/journeys${membershipNumber ? `?membershipNumber=${membershipNumber}` : ''}`);

  const [rebook, { loading: rebookLoading }] = useMutation(REBOOK);
  const [overridePrice, { loading: overridePriceLoading }] = useMutation(OVERRIDE_PRICE);
  const [overridePricesSummatedFares, { loading: overridePricesSummatedFaresLoading }] = useSummatedFaresOverrideMutation();

  const [updateBooking, { loading: updateBookingLoading }] = useMutation(UPDATE_EXTRAS, {
    variables: feeRequest,
    onCompleted: () => redirectOnCompleted()
  });

  const [addAdditionalDetails, { loading: addAdditionalDetailsLoading }] = useMutation(ADD_ADDITIONAL_DETAILS);

  const handleConfirmExchange = async () => {
    try {
      let replaceItems = [];

      if (!overrideReason) {
        const shouldUseReplaceItems =
          isMultiJourney ||
          (isProvisional &&
            (isExchangingMultipleLegsOfAOutboundSummated ||
              isExchangingSinglegOfAOutboundSummated ||
              isExchangingMultipleLegsOfAInboundSummated ||
              isExchangingSingleLegOfAInboundSummated));

        if (shouldUseReplaceItems) {
          replaceItems = getReplaceItems(
            data?.outboundSelectedFare,
            data?.inboundSelectedFare,
            outboundLegsBeingExchanged,
            inboundLegsBeingExchanged
          );
        }
      }

      const rebookResult = await rebook({
        variables: {
          ...esRequest,
          replaceItems
        }
      });

      let allUpdatePrices = [];
      let allUpdateAdditionalDetails = [];

      if (outboundOriginalClassOfService) {
        const newOutboundProducts =
          rebookResult?.data?.rebook?.pendingCreationLegs
            .filter(({ direction }) => direction === OUTBOUND)
            .map(({ bookingItems }) => bookingItems)
            .flat() ?? [];
        const oldOutboundProducts = data?.existingTrains?.outbound?.products ?? [];

        if (
          data?.isAOPActivated &&
          data?.isMaintainPriceActivated &&
          outboundOriginalClassOfService === data?.outboundSelectedFare?.classOfService?.code
        ) {
          const { updatePrices, updateAdditionalDetails } = getUpdateDetailsForOnHoldBooking(
            oldOutboundProducts,
            newOutboundProducts,
            true
          );
          allUpdatePrices = allUpdatePrices.concat(updatePrices);
          allUpdateAdditionalDetails = allUpdateAdditionalDetails.concat(updateAdditionalDetails);
        } else {
          const { updatePrices, updateAdditionalDetails } = getUpdateDetailsForOnHoldBooking(oldOutboundProducts, newOutboundProducts);
          allUpdatePrices = allUpdatePrices.concat(updatePrices);
          allUpdateAdditionalDetails = allUpdateAdditionalDetails.concat(updateAdditionalDetails);
        }
      }

      if (inboundOriginalClassOfService) {
        const newInboundProducts =
          rebookResult?.data?.rebook?.pendingCreationLegs
            .filter(({ direction }) => direction === INBOUND)
            .map(({ bookingItems }) => bookingItems)
            .flat() ?? [];
        const oldInboundProducts = data?.existingTrains?.inbound?.products ?? [];

        if (
          data?.isAOPActivated &&
          data?.isMaintainPriceActivated &&
          inboundOriginalClassOfService === data?.inboundSelectedFare?.classOfService?.code
        ) {
          const { updatePrices, updateAdditionalDetails } = getUpdateDetailsForOnHoldBooking(oldInboundProducts, newInboundProducts, true);
          allUpdatePrices = allUpdatePrices.concat(updatePrices);
          allUpdateAdditionalDetails = allUpdateAdditionalDetails.concat(updateAdditionalDetails);
        } else {
          const { updatePrices, updateAdditionalDetails } = getUpdateDetailsForOnHoldBooking(oldInboundProducts, newInboundProducts);
          allUpdatePrices = allUpdatePrices.concat(updatePrices);
          allUpdateAdditionalDetails = allUpdateAdditionalDetails.concat(updateAdditionalDetails);
        }
      }

      if (allUpdatePrices?.length > 0 && allUpdateAdditionalDetails?.length > 0) {
        await overridePrice({
          variables: {
            reference: data.reference,
            items: allUpdatePrices
          }
        });

        await addAdditionalDetails({
          variables: {
            bookingReference: data.reference,
            additionalDetails: allUpdateAdditionalDetails
          }
        });
      }

      if (isSummatedFaresSelectionEnabled) {
        await overridePricesSummatedFares({
          variables: {
            rebookData: rebookResult?.data?.rebook,
            originalData: data,
            reference: data.reference,
            isExchangingMultipleLegsOfAOutboundSummated,
            isExchangingSinglegOfAOutboundSummated,
            isExchangingMultipleLegsOfAInboundSummated,
            isExchangingSingleLegOfAInboundSummated,
            isExchangingOutboundDirectFare,
            isExchangingInboundDirectFare,
            isExchangingOutboundThroughFare,
            isExchangingInboundThroughFare,
            isFreeExchange: data?.isAOPActivated && data?.isMaintainPriceActivated,
            exchangingOutboundSameClassOfService: outboundOriginalClassOfService === data?.outboundSelectedFare?.classOfService?.code,
            exchangingInboundSameClassOfService: inboundOriginalClassOfService === data?.inboundSelectedFare?.classOfService?.code
          }
        });
      }

      if (hasFeeRequestSegments) {
        await updateBooking();
      }

      if (isStationAgent && localStorage.getItem(MOBILE_TERMINAL_ID_STORAGE_KEY)) {
        await addAdditionalDetails({
          variables: {
            bookingReference: data.reference,
            additionalDetails: [
              {
                key: `tablet-exchange-${moment().format('HH:mm:ss.DDMMYY')}`,
                value: stationName
              }
            ]
          }
        });
      }

      redirectOnCompleted();
    } catch (e) {
      setError(e);
    }
  };

  if (error) {
    throw new AgentPortalError(error);
  }

  const getMessaging = (showDowngradeMessage, loyaltyBooking) => {
    const alwaysDisplay = `
    ${formatMessage({ id: 'exchange.confirmation.modal.firstSubtitle' })}
    ${formatMessage({ id: 'exchange.confirmation.modal.secondSubtitle' })}
    `;

    if (showDowngradeMessage) {
      return loyaltyBooking
        ? `
      ${alwaysDisplay}
      3. ${formatMessage({ id: 'exchange.tryingToDowngradeMessage' })}
      4. ${formatMessage({ id: 'exchange.tryingToDowngradeWithPointsMessage' })}
      `
        : `
      ${alwaysDisplay}
      3. ${formatMessage({ id: 'exchange.tryingToDowngradeMessage' })}`;
    }

    return alwaysDisplay;
  };

  return (
    <>
      <StyledActionButton disabled={disabled} data-cy="confirm-exchange" data-testid="confirm-exchange" onClick={onOpen}>
        <FormattedMessage id="exchanges.continue" />
      </StyledActionButton>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={formatMessage({ id: 'exchange.confirmation.modal.title' })}
        subtitle={getMessaging(shouldShowDowngradeMessage, isLoyaltyBooking)}
      >
        <ConfirmButton
          data-cy="exchanges-continue"
          onClick={handleConfirmExchange}
          disabled={
            disabled ||
            rebookLoading ||
            updateBookingLoading ||
            addAdditionalDetailsLoading ||
            overridePricesSummatedFaresLoading ||
            overridePriceLoading
          }
          loading={
            rebookLoading ||
            updateBookingLoading ||
            addAdditionalDetailsLoading ||
            overridePricesSummatedFaresLoading ||
            overridePriceLoading
          }
        >
          <FormattedMessage id="exchanges.continue" />
        </ConfirmButton>
        <StyledActionButton onClick={onClose}>
          <FormattedMessage id="exchanges.adandon" />
        </StyledActionButton>
      </Modal>
    </>
  );
};

ExchangeConfirmSelectionWithWarning.propTypes = {
  intl: intlShape,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  isOpen: PropTypes.bool,
  history: historyShape.isRequired,
  data: PropTypes.shape({
    outboundSelectedFare: fareShape,
    inboundSelectedFare: fareShape,
    reference: PropTypes.string.isRequired,
    currency: PropTypes.string.isRequired,
    existingTrains: PropTypes.shape({
      inbound: PropTypes.shape({
        products: productsShape
      }),
      outbound: PropTypes.shape({
        products: productsShape
      })
    }),
    isAOPActivated: PropTypes.bool,
    isMaintainPriceActivated: PropTypes.bool
  }).isRequired,
  disabled: PropTypes.bool,
  overrideReason: PropTypes.string,
  isFlowForward: PropTypes.bool,
  isStationAgent: PropTypes.bool,
  isExchangingOFETrain: PropTypes.bool,
  outboundOriginalClassOfService: PropTypes.string,
  inboundOriginalClassOfService: PropTypes.string,
  isLoyaltyBooking: PropTypes.bool,
  hasSpecialLoyaltyTier: PropTypes.bool,
  stationName: PropTypes.string,
  isMultiJourney: PropTypes.bool,
  isSummatedFaresSelectionEnabled: PropTypes.bool,
  isExchangingMultipleLegsOfAOutboundSummated: PropTypes.bool,
  isExchangingMultipleLegsOfAInboundSummated: PropTypes.bool,
  isExchangingOutboundDirectFare: PropTypes.bool,
  isExchangingInboundDirectFare: PropTypes.bool,
  isExchangingOutboundThroughFare: PropTypes.bool,
  isExchangingInboundThroughFare: PropTypes.bool,
  outboundOriginalClassesOfAllServices: PropTypes.arrayOf(PropTypes.string),
  inboundOriginalClassesOfAllServices: PropTypes.arrayOf(PropTypes.string),
  isExchangingSinglegOfAOutboundSummated: PropTypes.bool,
  isExchangingSingleLegOfAInboundSummated: PropTypes.bool,
  isProvisional: PropTypes.bool,
  outboundLegsBeingExchanged: PropTypes.arrayOf(legShape),
  inboundLegsBeingExchanged: PropTypes.arrayOf(legShape)
};

ExchangeConfirmSelectionWithWarning.defaultProps = {
  isExchangingOFETrain: false
};

export default ExchangeConfirmSelectionWithWarning;
